/** Where the preview sidebar's width is kept between dialog opens. */
export const PREVIEW_SIDEBAR_WIDTH_KEY = "IngestAsAsset.sidebarWidth_in_pixels";

/**
 * Persistent storage (localStorage) for the ingest preview sidebar's width.
 *
 * ## Why this persists at all
 *
 * The reason a user widens this column is that their topic paths are long —
 * `ik_controller:joint_state.right_arm_joints_rad[3]` is a realistic one — and
 * that is a property of *their org's data* and *their screen*, neither of which
 * changes between one robot's ingest and the next. Someone ingesting a fleet's
 * worth of URDFs would otherwise re-drag the same column for every file. Both of
 * the app's other resizable sidebars (the nav sidebar, the workspace sidebar)
 * make the same call and store their width the same way, so this is the
 * established answer rather than a new one.
 *
 * The wizard has no settings store of its own, hence a module here rather than a
 * field on one: the workspace sidebar's own `sidebarStorage` sits beside its
 * component for the same reason.
 *
 * Nothing here clamps. The stored number is a *remembered drag*, not a layout
 * decision: the bounds live in `PreviewStep.module.css` as `min-width` /
 * `max-width`, so a width saved on a wide monitor and restored on a laptop is
 * gated by the same rules a fresh drag is, and there is no second copy of the
 * limits in JavaScript to drift from them.
 */
export const PreviewSidebarStorage = {
  /** The stored width in pixels, or `null` if none was stored or it was junk. */
  getWidth(): number | null {
    const value = localStorage.getItem(PREVIEW_SIDEBAR_WIDTH_KEY);
    if (value === null) {
      return null;
    }
    const width = Number(value);
    // A non-finite or non-positive value would be written straight into
    // `style.width`, where it is either ignored or collapses the column; either
    // way it is not a width, so it reads as "nothing stored".
    return Number.isFinite(width) && width > 0 ? width : null;
  },

  /** Remembers `value` as the width to open the sidebar at next time. */
  setWidth(value: number): void {
    localStorage.setItem(PREVIEW_SIDEBAR_WIDTH_KEY, String(Math.round(value)));
  },

  /** Forgets the stored width, so the sidebar opens at its CSS default. */
  clearWidth(): void {
    localStorage.removeItem(PREVIEW_SIDEBAR_WIDTH_KEY);
  },
};
